import React, { useState } from 'react';
import './ProductCard.less';
import {
  Card, Image, Typography, Tag,
} from 'antd';
import { PRODUCT_TYPE_TAGS } from 'config/tags';
import { useMediaQuery } from 'react-responsive';
import { productProperties } from 'config/language';
import { groupBy } from 'lodash';
import { stripProductSkuShortLabel } from 'util/sku-formatter';
import MicroRequiredIndicator from 'components/MicroRequiredIndicator/MicroRequiredIndicator';

const { Title } = Typography;

const CONTAINER_LABELS = { can: 'Cans', bottle: 'Bottles', keg: 'Kegs' };
const CONTAINER_ORDER = ['can', 'bottle', 'keg'];

const ProductCard = ({ product, skus }) => {
  const {
    name,
    requiresMicroReadings,
    oneLiner,
    type,
    blurb,
    tastingNotes,
    story,
    artist,
    tapBadgeUrl,
    dateReleased,
    releaseMonthUnknown,
    abv,
    abvMin,
    abvMax,
    malts,
    hops,
    accolades,
  } = product;

  const dualColumn = useMediaQuery({
    query: '(min-width: 1200px)',
  });

  const skuGroups = skus?.length ? groupBy(skus, 'containerType') : null;

  return (
    <Card className="product-card">
      {!dualColumn && <TapBadge imageUrl={tapBadgeUrl} /> }
      <div className="columns-container">
        <div className="column" id="left-column">
          <Title level={1}>{name}</Title>
          <Title level={4}>
            {oneLiner}
            <Tag color={PRODUCT_TYPE_TAGS[type]}>
              {type}
            </Tag>
            <MicroRequiredIndicator checked={!!requiresMicroReadings} />
          </Title>
          <InfoItem key="blurb" title={productProperties.blurb.labels.field} text={blurb} truncateAt={500} />
          <InfoItem key="tastingNotes" title={productProperties.tastingNotes.labels.field} text={tastingNotes} />
          <InfoItem key="story" title={productProperties.story.labels.field} text={story} truncateAt={500} />
          <InfoItem key="artist" title={productProperties.artist.labels.field} text={artist} />

          {skuGroups && (
            <div className="sku-section">
              <Title level={5}>SKUs</Title>
              {CONTAINER_ORDER.map((containerType) => {
                const group = skuGroups[containerType];
                if (!group?.length) return null;
                return (
                  <div key={containerType} className="sku-group">
                    <span className="sku-group-label">{CONTAINER_LABELS[containerType]}</span>
                    <span>
                      {group
                        .map((s) => stripProductSkuShortLabel(s.name, name || ''))
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="column" id="right-column">
          {dualColumn && <TapBadge imageUrl={tapBadgeUrl} /> }
          <InfoItem key="dateReleased" title={productProperties.dateReleased.labels.field} text={dateReleased?.format(releaseMonthUnknown ? 'YYYY' : 'MMMM YYYY')} />
          <InfoItem
            key="abv"
            title={productProperties.abv.labels.field}
            text={(() => {
              const abvIsRange = abvMin !== null || abvMax !== null;

              if (abv === null && !abvIsRange) {
                return null;
              }

              return (abv !== null ? (
                `${abv}%`
              ) : (
                `${abvMin === null ? '?' : `${abvMin}%`} - ${abvMax === null ? '?' : `${abvMax}%`}`
              ));
            })()}
          />
          <InfoItem key="malts" title={productProperties.malts.labels.field} text={malts} />
          <InfoItem key="hops" title={productProperties.hops.labels.field} text={hops} />
          <InfoItem key="accolades" title={productProperties.accolades.labels.field} text={accolades} />
        </div>
      </div>
    </Card>
  ); };

const InfoItem = ({ title, text, truncateAt }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const shouldTruncate = truncateAt && text.length > truncateAt && !expanded;

  return (
    <div className="info-item">
      <Title level={5}>{title}</Title>
      <p>
        {shouldTruncate ? text.slice(0, truncateAt) : text}
        {shouldTruncate && (
          <span
            className="expand-link"
            onClick={() => setExpanded(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setExpanded(true);
              }
            }}
            role="button"
            tabIndex={0}
          >
            {' '}
            . . .
          </span>
        )}
      </p>
    </div>
  );
};

const TapBadge = ({ imageUrl }) => (
  <Image
    className="tap-badge-image"
    width={200}
    height={200}
    preview
    src={imageUrl || '/assets/generic-tap-badge.png'}
    fallback="data:image/png;base64,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"
  />
);

export default ProductCard;
